import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Checkbox, RoutalPalette } from '@nx-smartmonkey/ui';
import clsx from 'clsx';
import memoize from 'memoize-one';
import { useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

import Text from '../../../../react-components/Text';
import VirtualizedDataTableHeaderCell from './VirtualizedDataTableHeaderCell';

const columnsStyles = () => ({
  checkboxColumn: {
    padding: `6px 12px 6px 12px`,
  },
  tableHead: {
    backgroundColor: `white`,
    fontSize: `14px`,
    textTransform: `capitalize!important` as any,
    color: RoutalPalette.primary20,
    fontWeight: 500,
    borderBottom: `unset`,
  },
  row: {
    display: `flex`,
    flexDirection: `row` as any,
    alignItems: `center`,
  },
});

type TableColumnProps = {
  domId: string;
  headerTableIsBiggestThanTableColumns: boolean;
  showCheckboxes?: boolean;
  selectedDocs: {
    id: string;
  }[];
  classes: {
    checkboxColumn: string;
    tableHead: string;
    row: string;
  };
  columns: Array<VirtualizedDataTableColumnProps>;
  docs?: {}[];
  sortBy?: string;
  sortDirection?: SortDirections;
  width?: string;
  handleSelectAll: (...args: any[]) => any;
  onColumnSort?: (column: string, order: SortDirections) => any;
};

const TableColumns = ({
  domId,
  headerTableIsBiggestThanTableColumns,
  showCheckboxes,
  selectedDocs,
  classes,
  columns,
  docs,
  sortBy,
  sortDirection,
  handleSelectAll,
  onColumnSort,
}: TableColumnProps) => {
  const [checkboxRowHover, setCheckboxRowHover] = useState(false);
  return (
    <TableRow
      id={domId}
      component="div"
      className={classes.row}
      style={{
        ...(!headerTableIsBiggestThanTableColumns ? { borderBottom: `1px solid rgba(224, 224, 224, 1)` } : {}),
      }}
    >
      {showCheckboxes && docs && docs.length > 0 && (
        <TableCell
          component="div"
          className={classes.checkboxColumn}
          classes={{ head: classes.tableHead }}
          onMouseEnter={() => setCheckboxRowHover(true)}
          onMouseLeave={() => setCheckboxRowHover(false)}
        >
          {checkboxRowHover || (selectedDocs && selectedDocs.length > 0) ? (
            <Checkbox
              checked={selectedDocs.length === docs.length}
              onChange={handleSelectAll}
              indeterminate={selectedDocs.length < docs.length && selectedDocs.length > 0}
            />
          ) : (
            <div style={{ width: `24px`, height: `24px` }} />
          )}
        </TableCell>
      )}
      {columns.map((column: VirtualizedDataTableColumnProps) => {
        column.style = {
          ...(column.style ?? {}),
          padding: `6px 12px 6px 12px`,
          borderBottom: `unset`,
          ...(column.width ? { width: `${column.width}px` } : {}),
        };

        return (
          <VirtualizedDataTableHeaderCell
            key={`table-head-${column.accessorName}`}
            column={column}
            onColumnSort={onColumnSort}
            active={sortBy === column.accessorName}
            sortDirection={sortBy === column.accessorName ? sortDirection : undefined}
          />
        );
      })}
    </TableRow>
  );
};

const StyledTableColumns = withStyles(columnsStyles)(TableColumns);

const rowsStyles = () => ({
  selected: {
    '&$selected': {},
  },
  hover: {
    '&$hover:hover': {},
  },
  row: {
    display: `flex`,
    flexDirection: `row` as any,
    flexWrap: `nowrap` as any,
    alignItems: `center`,
    boxSizing: `border-box` as any,
    minWidth: `100%`,
    width: `100%`,
    padding: 0,
    borderBottom: `1px solid rgba(224, 224, 224, 1)`,
  },
  placeholder: {
    color: `#8194B8`,
    paddingLeft: `24px`,
    fontSize: `12px`,
  },
  cell: {
    display: `block`,
    flexGrow: 0,
    flexShrink: 0,
    padding: `0 12px`,
  },
  checkboxColumn: {
    // width: '36px'
  },
  expandingCell: {
    flex: 1,
  },
});

const ROW_SIZE = 45;

type RowProps = {
  classes: {
    selected: string;
    hover: string;
    row: string;
    cell: string;
    checkboxColumn: string;
    expandingCell: string;
  };
  index: number;
  style?: any;
  data: {
    classes?: any;
    items: any[];
    columns: Array<VirtualizedDataTableColumnProps>;
    selectedDocs: {}[];
    showCheckboxes?: boolean;
    onRowDoubleClick: (...args: any[]) => any;
    handleRowSelect: (...args: any[]) => any;
    handleCheckboxSelect: (...args: any[]) => any;
    handleOnContextMenu: (...args: any[]) => any;
  };
};

const Row = ({ classes, index, style, data /* , isScrolling */ }: RowProps) => {
  const {
    showCheckboxes,
    items,
    columns,
    // classes: cellClasses,
    selectedDocs,
    onRowDoubleClick,
    handleRowSelect,
    handleCheckboxSelect,
    handleOnContextMenu,
  } = data;

  const [rowHover, setRowHover] = useState(false);

  const [checkboxRowHover, setCheckboxRowHover] = useState(false);

  const item = items[index];

  // IMPROVEMENT: Use isScrolling for showing a placeholder on scroll.
  // if (isScrolling) {
  //   return (
  //     <TableRow component="div" className={clsx(classes.row, classes.placeholder)} style={style}>
  //       <span>Loading...</span>
  //     </TableRow>
  //   );
  // }

  return (
    <TableRow
      component="div"
      className={classes.row}
      style={style}
      onClick={(event: any) => {
        event.stopPropagation();

        // This was old. Now the click in the entire row is not allowed, only in its checkbox.
        // if (event.ctrlKey || event.metaKey) {
        //   handleCheckboxSelect(item.doc);
        // } else {
        //   handleRowSelect(item.doc);
        // }

        handleRowSelect(item.doc);
      }}
      onContextMenu={(e: any) => {
        if (handleOnContextMenu) handleOnContextMenu(e, item.doc);
      }}
      onDoubleClick={() => onRowDoubleClick(item.doc)}
      selected={selectedDocs.find((doc: any) => doc.id === item.doc.id) !== undefined}
      classes={{ selected: classes.selected, hover: classes.hover }}
      hover={rowHover}
      onMouseEnter={() => setRowHover(true)}
      onMouseLeave={() => setRowHover(false)}
    >
      {showCheckboxes && (
        <TableCell
          component="div"
          onClick={(event) => {
            event.stopPropagation();
            handleCheckboxSelect(item.doc);
          }}
          className={clsx(classes.cell, classes.checkboxColumn)}
          style={{
            height: `100%`,
            display: `flex`,
            flexDirection: `row` as any,
            alignItems: `center`,
          }}
          onMouseEnter={() => setCheckboxRowHover(true)}
          onMouseLeave={() => setCheckboxRowHover(false)}
        >
          {checkboxRowHover || (selectedDocs && selectedDocs.length > 0) ? (
            <Checkbox checked={selectedDocs.find((doc: any) => doc.id === item.doc.id) !== undefined} />
          ) : (
            <>{item.checkboxIcon}</>
          )}
        </TableCell>
      )}

      {columns.map((columnElement: VirtualizedDataTableColumnProps, colIndex: any) => {
        let justifyContent = null;

        if (columnElement.align === `right`) justifyContent = `flex-end`;
        if (columnElement.align === `left`) justifyContent = `flex-start`;
        if (columnElement.align === `center`) justifyContent = `center`;

        return (
          <TableCell
            key={item.doc.id + colIndex}
            component="div"
            variant="body"
            align={columnElement.align}
            className={clsx(classes.cell, !columnElement.width && classes.expandingCell)}
            style={{
              width: columnElement.width ? `${columnElement.width}px` : `100%`,
              height: `100%`,

              display: `flex`,
              flexDirection: `row` as any,
              alignItems: `center`,
              ...(columnElement.flex ? { flex: columnElement.flex } : {}),
              ...(justifyContent ? { justifyContent } : {}),
            }}
          >
            <Typography
              component="span"
              noWrap
              style={{
                width: `100%`,
                maxWidth: `${columnElement.width}px`,
                ...(justifyContent ? { display: `flex`, justifyContent } : {}),
              }}
            >
              {(() => {
                if (columnElement.formatter) {
                  return columnElement.formatter({
                    value: item.doc[columnElement.accessorName ?? ``],
                    doc: item.doc,
                    index,
                    rowHover,
                  });
                }
                return <Text variant="body">{item.doc[columnElement.accessorName ?? ``]}</Text>;
              })()}
            </Typography>
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const StyledRow = withStyles(rowsStyles)(Row);

const itemKey = (index: any, data: any) => data.items[index].id;

const createItemData = memoize((classes, columns, data) => ({
  classes,
  columns,
  items: data,
}));

const tableStyles = {
  table: {
    display: `flex`,
    flexDirection: `column` as any,
    flex: `1`,
    height: `100%`,
    width: `100%`,
  },
  list: {},
  thead: {
    display: `flex`,
    flexDirection: `row` as any,
    alignItems: `center`,
  },
  tbody: {
    display: `flex`,
    flexDirection: `column` as any,
    flex: `1`,
    width: `100%`,
  },
};

export interface VirtualizedDataTableColumnProps {
  style?: React.CSSProperties;
  width?: number | string;
  height?: number;
  align?: AlignmentTypes;
  sortable?: boolean;
  name?: string | React.ReactElement;
  accessorName?: string;
  flex?: number;
  loadingContent?: React.ReactElement;
  formatter?: ({ value, doc, index, rowHover }: { value: any; doc: any; index: number; rowHover: boolean }) => any;
}

export type SortDirections = `asc` | `desc`;

export type AlignmentTypes = `left` | `right` | `inherit` | `center` | `justify`;

type VirtualizedMaterialDataTableProps<T> = {
  docs?: T[];
  classes: {
    table: string;
    list: string;
    thead: string;
    tbody: string;
  };
  columns: Array<VirtualizedDataTableColumnProps>;
  selectedDocs?: {
    id: string;
  }[];
  showCheckboxes?: boolean;
  onRowDoubleClick?: (...args: any[]) => any;
  onRowSelect?: (...args: any[]) => any;
  handleOnContextMenu?: (...args: any[]) => any;
  onMultipleSelection?: (...args: any[]) => any;
  initialSortBy?: string;
  initialSortOrder?: SortDirections;
  onColumnSort?: (column: string, order: SortDirections) => any;
};

const VirtualizedDataTable = ({
  classes,
  docs,
  selectedDocs,
  columns,
  showCheckboxes,
  onRowDoubleClick,
  onRowSelect,
  handleOnContextMenu,
  onMultipleSelection,
  initialSortBy,
  initialSortOrder,
  onColumnSort,
}: VirtualizedMaterialDataTableProps<{ doc: any; checkboxIcon?: React.ReactElement }>) => {
  const [dimensions, setDimensions] = useState<{ height: number; width: number } | undefined>(undefined);
  const [headerTableIsBiggestThanTableColumns, setHeaderIsBiggestThanHeaderColumns] = useState(false);

  const itemData: Record<string, any> = createItemData(classes, columns, docs);
  itemData[`showCheckboxes`] = showCheckboxes;
  itemData[`selectedDocs`] = selectedDocs;
  itemData[`onRowDoubleClick`] = onRowDoubleClick;
  itemData[`handleOnContextMenu`] = handleOnContextMenu;

  /**
   * Function is called when a single row is selected
   * @param {Object} doc
   */
  itemData[`handleRowSelect`] = (doc: any) => {
    if (onRowSelect) {
      onRowSelect(doc);
      if (onMultipleSelection) {
        const newSelection = [doc];
        onMultipleSelection(newSelection);
      }
    }
  };

  /**
   * This function is called when the checkbox is clicked
   * @param {Object} doc
   */
  itemData[`handleCheckboxSelect`] = (doc: any) => {
    let newSelection = [];
    if ((selectedDocs ?? []).find((selectedDoc: any) => selectedDoc.id === doc.id) !== undefined) {
      newSelection = (selectedDocs ?? []).filter((selectedDoc: any) => selectedDoc.id !== doc.id);
    } else {
      newSelection = [...(selectedDocs ?? []), doc];
    }
    if (onMultipleSelection) {
      onMultipleSelection(newSelection);
    }
  };

  /**
   * Select each single element on the current client page
   */
  const handleSelectAll = () => {
    if (onMultipleSelection) {
      const newSelection = ((selectedDocs ?? []).length === 0 ? [...(docs ?? [])] : []).map((item) => item.doc);
      onMultipleSelection(newSelection);
    }
  };

  // TODO: On resize, set dimensions to undefined.

  return (
    <Table className={classes.table} stickyHeader component="div">
      <TableHead
        id="table-header"
        component="div"
        className={classes.thead}
        style={{
          ...(headerTableIsBiggestThanTableColumns ? { borderBottom: `1px solid rgba(224, 224, 224, 1)` } : {}),
        }}
      >
        <StyledTableColumns
          domId="table-header-columns"
          headerTableIsBiggestThanTableColumns={headerTableIsBiggestThanTableColumns}
          showCheckboxes={showCheckboxes}
          handleSelectAll={handleSelectAll}
          selectedDocs={selectedDocs ?? []}
          columns={columns}
          docs={docs}
          sortDirection={initialSortOrder}
          sortBy={initialSortBy}
          onColumnSort={(sortBy: string, sortDirection: SortDirections) => {
            if (onColumnSort) {
              onColumnSort(sortBy, sortDirection);
            }
          }}
        />
      </TableHead>

      {docs !== undefined && docs.length > 0 && (
        <TableBody
          component="div"
          className={classes.tbody}
          style={{
            height: dimensions !== undefined ? `${dimensions.height}px` : `100%`,
          }}
        >
          <AutoSizer>
            {({ height, width }: any) => {
              setTimeout(() => {
                const tHeader = document.getElementById(`table-header`);
                const tHeaderColumns = document.getElementById(`table-header-columns`);

                if (tHeader && tHeaderColumns) {
                  let headerWidth: number;
                  if (tHeader.offsetWidth >= tHeaderColumns.offsetWidth) {
                    headerWidth = tHeader.offsetWidth;
                  } else {
                    headerWidth = tHeaderColumns.offsetWidth;
                  }

                  // Only updating the state if width has changed.
                  if (dimensions === undefined || dimensions.width !== headerWidth) {
                    setDimensions({
                      height,
                      width: headerWidth,
                    });
                    if (tHeader.offsetWidth >= tHeaderColumns.offsetWidth) {
                      setHeaderIsBiggestThanHeaderColumns(true);
                    } else {
                      setHeaderIsBiggestThanHeaderColumns(false);
                    }
                  }
                }
              });

              return (
                <List
                  className={classes.list}
                  height={dimensions !== undefined ? dimensions.height : height}
                  width={dimensions !== undefined ? dimensions.width : width}
                  itemCount={docs.length}
                  itemSize={ROW_SIZE}
                  itemKey={itemKey}
                  itemData={itemData}
                  useIsScrolling
                  style={{
                    overflowX: `hidden`,
                  }}
                >
                  {/* @ts-expect-error */}
                  {StyledRow}
                </List>
              );
            }}
          </AutoSizer>
        </TableBody>
      )}
    </Table>
  );
};

VirtualizedDataTable.defaultProps = {
  docs: undefined,
  maxHeight: undefined,
  showCheckboxes: false,
  selectedDocs: [],

  sortBy: undefined,
  sortDirection: undefined,
};

export default withStyles(tableStyles)(VirtualizedDataTable);
